import JSZip from "jszip";
import type {
	DefaultStyle,
	DefaultStyleCollection,
} from "@mappedin/mvf/default-style";
import type {
	FloorCollection,
	FloorId,
	GeometryCollection,
	GeometrySchema,
} from "@mappedin/mvf/core";
import type { Location, LocationInstance } from "@mappedin/mvf/locations";

const GEOMETRY_FILE_PATH = /^geometry\/(.*)\.geojson$/;

interface MVFBundle {
	floors?: FloorCollection;
	defaultStyle?: DefaultStyleCollection;
	locations?: Location[];
	locationInstances?: LocationInstance[];
	geometry: GeometrySchema["geometry"];
}

async function readZipFile<T>(file: JSZip.JSZipObject): Promise<T | undefined> {
	try {
		const text = await file.async("string");
		return JSON.parse(text) as T;
	} catch (error) {
		throw new Error(
			`Failed to parse Mappedin MVF file "${file.name}": ${String(error)}`,
		);
	}
}

async function readZipFilePath<T>(
	zip: JSZip,
	path: string,
): Promise<T | undefined> {
	const file = zip.file(path);
	if (file === null) {
		return undefined;
	}

	return readZipFile<T>(file);
}

export async function readMVFBundle(zipData: ArrayBuffer): Promise<MVFBundle> {
	//Load the ZIP file
	let zip: JSZip;
	try {
		zip = await JSZip.loadAsync(zipData);
	} catch (error) {
		throw new Error(`Failed to load Mappedin MVF zip: ${String(error)}`);
	}

	//Load geometry files
	const geometry: GeometrySchema["geometry"] = {};
	for (const [path, file] of Object.entries(zip.files)) {
		const match = path.match(GEOMETRY_FILE_PATH);
		if (match === null) {
			continue;
		}
		const floorId = match[1] as FloorId;
		const collection = await readZipFile<GeometryCollection>(file);
		if (collection !== undefined) {
			geometry[floorId] = collection;
		}
	}

	//Read floors
	const floors = await readZipFilePath<FloorCollection>(
		zip,
		"floors.geojson",
	);

	//Read default style
	const defaultStyle = await readZipFilePath<DefaultStyleCollection>(
		zip,
		"default-style.json",
	);

	//Read locations
	const locations = await readZipFilePath<Location[]>(zip, "locations.json");

	//Read location instances
	const locationInstances = await readZipFilePath<LocationInstance[]>(
		zip,
		"location-instances.json",
	);

	return {
		floors,
		defaultStyle,
		locations,
		locationInstances,
		geometry,
	};
}
